/**
 * Prolonged disruption, on the Crisis Desk — the screen.
 *
 * The playbooks above it answer "what happens in the first quarter of an
 * hour". This answers "and if it is still happening in March": the postures
 * priced against the airport's own finance, the four duration bands with what
 * each one forces, the levers with the two the platform can price and the two
 * it cannot, the restart plan read backwards from the reopening date, and the
 * floor of obligations that survives a full shutdown.
 *
 * Every assumption is labelled on the face of it. The posture factors are the
 * only invented numbers on this screen and they are marked wherever they
 * appear, because the one figure a reader would act on here is the date the
 * cash runs out.
 */
import { useState } from "react";
import { type ContinuityDesk, type ContinuityStage, POSTURE_BY_KEY } from "../../domain/continuity";
import { money } from "../../domain/intelligence";
import { Card } from "../../design/components";
import { t, tn } from "../../i18n";
import { Refs, Tiles, x as s } from "./shared";

export function ContinuityView({ desk }: { desk: ContinuityDesk }) {
  const [scenarioKey, setScenarioKey] = useState(desk.scenarios[0]?.key ?? "");
  const [stageKey, setStageKey] = useState<string | null>(null);
  const scenario = desk.scenarios.find((x) => x.key === scenarioKey) ?? desk.scenarios[0];
  if (!scenario) return null;
  const stage = scenario.stages.find((x) => x.key === stageKey) ?? scenario.stages[scenario.stages.length - 1];

  return (
    <div className={s.page} data-testid="continuity">
      <Card
        title="Prolonged disruption — force majeure, conflict, and airspace closed for an unknown period"
        sub="the events that do not end in an hour; the emergency plan hands over here"
      >
        <p className={s.lead}>
          {t(
            "An aerodrome emergency plan is written for an event with an end. These are the events without one. Each is staged by duration — 0–72 hours, 72 hours to four weeks, one to three months, and beyond three months or unknown — and each band carries what it forces on operations, on people, on spend, on the capital program and on the contracts. The postures are priced against the airport's own finance, so every one of them carries the date the cash runs out.",
          )}
        </p>
        <p className={s.muted}>
          {t(
            "These scenarios are a draft for approval, not a plan in force. The posture factors are planning assumptions and are marked wherever they are used; the money they are applied to is the platform's.",
          )}
        </p>
        <Tiles items={desk.tiles} currency={desk.currency} />
      </Card>

      <Card
        title="What each posture costs, and how long the cash lasts there"
        sub="the platform's finance, with a declared assumption applied"
      >
        <div className={s.tableWrap}>
          <table className={s.table}>
            <thead>
              <tr>
                <th>{t("Posture")}</th>
                <th className={s.num}>{t("Cost a day")}</th>
                <th className={s.num}>{t("Revenue a day")}</th>
                <th className={s.num}>{t("Net a day")}</th>
                <th className={s.num}>{t("Cash lasts")}</th>
                <th>{t("Runs out")}</th>
              </tr>
            </thead>
            <tbody>
              {desk.postures.map((p) => (
                <tr key={p.posture.key} data-testid={`posture-${p.posture.key}`}>
                  <td>
                    <b>{t(p.posture.name)}</b>
                    <div className={s.muted}>{t(p.posture.what)}</div>
                    <div className={s.muted}>
                      <span className={s.tag}>{t("assumption")}</span>{" "}
                      {t("{opex}% of cost, {rev}% of revenue — {basis}", {
                        opex: Math.round(p.posture.opexFactor * 100),
                        rev: Math.round(p.posture.revenueFactor * 100),
                        basis: t(p.posture.basis),
                      })}
                    </div>
                  </td>
                  <td className={s.num}>{p.dailyCost != null ? money(p.dailyCost, desk.currency) : "—"}</td>
                  <td className={s.num}>{p.dailyRevenue != null ? money(p.dailyRevenue, desk.currency) : "—"}</td>
                  <td className={`${s.num} ${p.dailyNet != null && p.dailyNet < 0 ? s.neg : s.pos}`}>
                    {p.dailyNet != null ? money(p.dailyNet, desk.currency) : "—"}
                  </td>
                  <td className={s.num}>
                    {p.runwayDays != null ? tn(p.runwayDays, "{n} day", "{n} days") : t("no cash position")}
                  </td>
                  <td>{p.runsOutOn ?? "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <Refs list={desk.postures[0]?.sources ?? []} wide />
      </Card>

      <section className={s.cards} aria-label={t("Scenarios")}>
        {desk.scenarios.map((x) => (
          <button
            key={x.key}
            type="button"
            className={`${s.card} ${s.select} ${x.key === scenario.key ? s.cardOpen : ""}`}
            onClick={() => {
              setScenarioKey(x.key);
              setStageKey(null);
            }}
            aria-pressed={x.key === scenario.key}
            data-testid={`scenario-${x.key}`}
          >
            <h3 className={s.cardTitle}>{t(x.name)}</h3>
            <p className={s.cardText}>{t(x.trigger)}</p>
            <p className={s.cardText}>
              <b>{t(x.character)}</b>
            </p>
          </button>
        ))}
      </section>

      <Card title={t(scenario.name)} sub={t(scenario.handover)}>
        <div className={s.colTitle}>{t("Who holds it")}</div>
        <p className={s.cardText}>{scenario.holds.map((h) => t(h)).join(" · ")}</p>

        <div className={s.colTitle}>{t("The bands, and what each one forces")}</div>
        <section className={s.cards} aria-label={t("Duration bands")}>
          {scenario.stages.map((st) => (
            <button
              key={st.key}
              type="button"
              className={`${s.card} ${s.select} ${st.key === stage.key ? s.cardOpen : ""}`}
              onClick={() => setStageKey(st.key)}
              aria-pressed={st.key === stage.key}
              data-testid={`stage-${scenario.key}-${st.key}`}
            >
              <div className={s.cardHead}>
                <span className={st.toDays === null ? s.tagRed : s.tag}>{t(st.band)}</span>
              </div>
              <h3 className={s.cardTitle}>{t(POSTURE_BY_KEY[st.posture].name)}</h3>
              <p className={s.cardText}>{t(st.decide)}</p>
            </button>
          ))}
        </section>
        <StageView stage={stage} />
      </Card>

      <Card
        title="The levers"
        sub="priced where the platform holds the record, and named as unpriced where it does not"
      >
        <dl className={s.kv}>
          {desk.levers.map((l) => (
            <div key={l.key} data-testid={`lever-${l.key}`}>
              <dt>{l.label}</dt>
              <dd>
                <b>{l.value}</b>
                <div className={s.muted}>{l.basis}</div>
                <Refs list={l.sources} />
              </dd>
            </div>
          ))}
        </dl>
      </Card>

      <Card
        title="Restarting"
        sub="read backwards from the day traffic returns; the lead times are the reason a reopening decision cannot be taken in the week it is wanted"
      >
        <div className={s.tableWrap}>
          <table className={s.table}>
            <thead>
              <tr>
                <th>{t("When")}</th>
                <th>{t("Phase, and the gate it must pass")}</th>
                <th>{t("What happens")}</th>
              </tr>
            </thead>
            <tbody>
              {scenario.restart.map((r) => (
                <tr key={r.key} data-testid={`restart-${r.key}`}>
                  <td className={s.num}>
                    <b>{t("T−{days} d", { days: r.leadDays })}</b>
                  </td>
                  <td>
                    <b>{t(r.name)}</b>
                    <div className={s.muted}>{t(r.gate)}</div>
                    <div className={s.muted}>{t("Shown by: {evidence}", { evidence: t(r.evidence) })}</div>
                  </td>
                  <td>
                    <ol className={s.plain}>
                      {r.actions.map((a) => (
                        <li key={a}>{t(a)}</li>
                      ))}
                    </ol>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <Card
        title="What continues when nothing flies"
        sub="the obligations a full shutdown does not remove — each is the thing a cost cut reaches for first, because nothing visibly breaks the week it is cut"
      >
        <div className={s.tableWrap}>
          <table className={s.table}>
            <tbody>
              {desk.floor.map((f) => (
                <tr key={f.what}>
                  <td>
                    <b>{t(f.what)}</b>
                    <div className={s.muted}>{t(f.why)}</div>
                    <div className={s.muted}>
                      <span className={s.tag}>{t("reference to check")}</span> {f.reference}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <Card
        title="Is the airport ready for a long one"
        sub="read from the records, and every gap names what would close it"
      >
        <div className={s.tableWrap}>
          <table className={s.table}>
            <tbody>
              {desk.checks.map((c) => (
                <tr key={c.key} data-testid={`continuity-check-${c.key}`}>
                  <td>
                    <span className={c.met ? s.tagGreen : s.tagRed}>{c.met ? t("met") : t("gap")}</span>
                  </td>
                  <td>
                    <b>{c.what}</b>
                    <div className={s.muted}>{c.basis}</div>
                    {c.todo && <div className={s.muted}>{t("To close it: {todo}", { todo: c.todo })}</div>}
                    <Refs list={c.sources} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {desk.gaps.length > 0 && (
        <Card title="What this desk does not know" sub="stated, so nothing here is read as more certain than it is">
          <ul className={s.plain}>
            {desk.gaps.map((g) => (
              <li key={g}>{g}</li>
            ))}
          </ul>
        </Card>
      )}
    </div>
  );
}

function StageView({ stage }: { stage: ContinuityStage }) {
  const columns: { key: keyof ContinuityStage; title: string }[] = [
    { key: "operations", title: "Operations" },
    { key: "people", title: "People" },
    { key: "spend", title: "Operating spend" },
    { key: "projects", title: "The capital program" },
    { key: "contracts", title: "Contracts" },
  ];
  return (
    <div data-testid={`stage-detail-${stage.key}`}>
      <div className={s.colTitle}>
        {t("{band} — {posture}", { band: t(stage.band), posture: t(POSTURE_BY_KEY[stage.posture].name) })}
      </div>
      <p className={s.cardText}>
        <b>{t("The decision this band forces: ")}</b>
        {t(stage.decide)}
      </p>
      {stage.irreversible && (
        <p className={s.cardText} data-testid={`irreversible-${stage.key}`}>
          <span className={s.tagRed}>{t("hard to undo")}</span> {t(stage.irreversible)}
        </p>
      )}
      <div className={s.two}>
        {columns.map((col) => {
          const items = stage[col.key] as string[];
          if (!items?.length) return null;
          return (
            <div key={col.key}>
              <div className={s.colTitle}>{t(col.title)}</div>
              <ul className={s.plain}>
                {items.map((item) => (
                  <li key={item}>{t(item)}</li>
                ))}
              </ul>
            </div>
          );
        })}
      </div>
    </div>
  );
}
